// Siktelinje-verktøy — «har OP-posten sikt til dette punktet?»
//
// Velg to punkt i kartet → EKTE svar over EKTE høydedata (Terrarium-DEM via
// @skade/terrain): fri eller blokkert, HVILKET punkt som blokkerer (markert på
// kartet), minste klaring, og hvor høyt observatøren må opp for fri sikt.
//
// Den viktigste visuelle biten er HØYDEPROFILEN: operatøren skal SE hvor
// terrenget skjærer sikten, ikke bare lese et ja/nei. Grafen er ren SVG/DOM —
// ingen nye avhengigheter — og viser tre kurver:
//   1. terrengsnittet (rå kote langs linja),
//   2. jordkrumnings-/refraksjonskorrigert terreng (terreng + d1·d2/2R_e), som er
//      DET siktlinja faktisk må klare, og
//   3. siktlinja mellom observatør- og målhøyde.
// Over korte avstander sammenfaller 1 og 2 (buen er centimetere); da tegnes bare
// terrengsnittet og grafens tegnforklaring sier det.
//
// ÆRLIGHET (VALE §1): `lineOfSightDetailed` returnerer null når høydedata mangler
// for et endepunkt. Det vises som «kan ikke svare» — ALDRI som fri sikt. Hull
// midt i profilen telles og rapporteres. Datagrunnlaget (DTM ~30 m, uten
// bygninger/skog) står alltid i panelet, fordi «fri sikt» i bebyggelse eller
// granskog ellers blir en farlig løgn.
import { forward as mgrsForward } from 'mgrs';
import type { GeoJSONSource, Map as MlMap, MapMouseEvent } from 'maplibre-gl';
import { featureCollection, type ElevationGrid, type GeoFeature, type LatLon } from '@skade/contracts';
import { TerrariumTileCache, lineOfSightDetailed, type LosResult, type LosSample } from '@skade/terrain';
import {
  DEM_KILDE_NOTE, armEksklusivt, avstandM, boksRundtPunkter, celleForAvstand, fmtAvstand, fmtHoyde,
  fmtVinkel, frigiArming, hentHoydedataForBoks, nf,
} from '../map/dekning.js';

// ── Tilstand (rent, DOM-fritt — derfor testbart) ─────────────────────────────

/** Forhåndsvalg for observatørhøyde over bakken. */
export const OBSERVATOR_FORVALG: [string, number][] = [
  ['Liggende 0,3 m', 0.3],
  ['Knestående 1,0 m', 1.0],
  ['Stående (øyehøyde) 1,7 m', 1.7],
  ['Kjøretøytak 2,5 m', 2.5],
  ['Tre / stige 8 m', 8],
  ['Tak 4. etasje 12 m', 12],
  ['Mast 15 m', 15],
  ['Mast 30 m', 30],
];

/** Forhåndsvalg for målhøyde over bakken. */
export const MAL_FORVALG: [string, number][] = [
  ['Bakkenivå 0 m', 0],
  ['Person 1,7 m', 1.7],
  ['Kjøretøy 2,5 m', 2.5],
  ['Drone lav 50 m', 50],
  ['Drone 100 m', 100],
  ['Helikopter 300 m', 300],
];

export type SiktPunktNavn = 'a' | 'b';

/**
 * Punkt- og høydetilstand for verktøyet. Tredje klikk starter en NY linje (setter
 * A og nullstiller B) — ellers ville operatøren måtte lete etter en «nullstill»
 * for hver ny måling.
 */
export class SiktTilstand {
  a: LatLon | null = null;
  b: LatLon | null = null;
  /** Hvilket punkt neste klikk setter. */
  neste: SiktPunktNavn = 'a';
  observatorHoydeM = 1.7;
  malHoydeM = 1.7;
  /** Ekstra klaringskrav (m) for skog/bebyggelse DTM-et ikke kjenner. */
  marginM = 0;

  /** Sett neste punkt fra et kartklikk. Returnerer hvilket punkt som ble satt. */
  settPunkt(lon: number, lat: number): SiktPunktNavn {
    if (this.neste === 'a') {
      this.a = { lat, lon };
      this.b = null;
      this.neste = 'b';
      return 'a';
    }
    this.b = { lat, lon };
    this.neste = 'a';
    return 'b';
  }

  /** Sett et bestemt punkt (kontekstmeny/koordinat-inntasting). */
  sett(navn: SiktPunktNavn, lon: number, lat: number): void {
    if (navn === 'a') { this.a = { lat, lon }; this.neste = this.b ? 'a' : 'b'; }
    else { this.b = { lat, lon }; this.neste = 'a'; }
  }

  /** Bytt observatør og mål (samme linje, motsatt spørsmål). */
  bytt(): void {
    const t = this.a; this.a = this.b; this.b = t;
  }

  klar(): boolean { return this.a !== null && this.b !== null; }

  nullstill(): void {
    this.a = null; this.b = null; this.neste = 'a';
  }

  /** Avstand A→B (m), 0 når linja ikke er komplett. */
  avstandM(): number {
    return this.a && this.b ? avstandM(this.a, this.b) : 0;
  }
}

// ── Sammendrag (rent) ────────────────────────────────────────────────────────

export type SiktStatus = 'fri' | 'blokkert' | 'ukjent';

export const SIKT_STATUS_TEKST: Record<SiktStatus, string> = {
  fri: 'FRI SIKT',
  blokkert: 'BLOKKERT AV TERRENG',
  ukjent: 'KAN IKKE SVARE — HØYDEDATA MANGLER',
};

export interface SiktSammendrag {
  status: SiktStatus;
  tittel: string;
  rader: [string, string][];
  advarsler: string[];
}

/**
 * Gjør et `LosResult` om til det operatøren skal lese. Status `ukjent` når alle
 * mellompunkt er hull: da har vi ingen klaring å påstå noe om, og `clear === false`
 * fra motoren betyr «vet ikke», ikke «blokkert».
 */
export function siktSammendrag(los: LosResult, marginM = 0): SiktSammendrag {
  const ukjent = Number.isNaN(los.minClearanceM);
  const status: SiktStatus = ukjent ? 'ukjent' : los.clear ? 'fri' : 'blokkert';
  const rader: [string, string][] = [
    ['Avstand', fmtAvstand(los.distanceM)],
    ['Observatør', `kote ${fmtHoyde(los.observerGroundElevM)} + ${fmtHoyde(los.observerAmslM - los.observerGroundElevM)} = ${fmtHoyde(los.observerAmslM)} AMSL`],
    ['Mål', `kote ${fmtHoyde(los.targetGroundElevM)} + ${fmtHoyde(los.targetAmslM - los.targetGroundElevM)} = ${fmtHoyde(los.targetAmslM)} AMSL`],
    ['Minste klaring', ukjent
      ? 'ukjent'
      : `${fmtHoyde(los.minClearanceM)} ved ${fmtAvstand(los.minClearanceAtM)}`],
  ];
  if (los.obstruction) {
    rader.push(['Bryter sikten', `${fmtAvstand(los.obstruction.distanceM)} ut · kote ${fmtHoyde(los.obstruction.terrainElevM)} · ${fmtHoyde(los.obstruction.penetrationM)} over siktlinja`]);
  }
  rader.push(
    ['Hev observatør', los.extraObserverHeightM > 0
      ? `+${fmtHoyde(los.extraObserverHeightM)} (totalt ${fmtHoyde(los.requiredObserverHeightM)} over bakken)`
      : 'ikke nødvendig'],
    ['Maskeringsvinkel', fmtVinkel(los.maskingAngleDeg)],
    ['Elevasjon til mål', fmtVinkel(los.targetAngleDeg)],
  );

  const advarsler: string[] = [];
  if (los.voidSamples > 0) {
    advarsler.push(
      `${los.voidSamples} av ${los.samples.length} profilpunkt mangler høydedata — ` +
      'resultatet er ufullstendig i disse partiene.',
    );
  }
  if (marginM > 0) {
    advarsler.push(`Krever ${fmtHoyde(marginM)} klaring (margin for skog/bebyggelse), ikke bare bar sikt.`);
  }
  if (status === 'fri') {
    advarsler.push('«Fri sikt» gjelder BAKKETERRENG. Bygninger, skog og master er ikke i datagrunnlaget.');
  }
  return { status, tittel: SIKT_STATUS_TEKST[status], rader, advarsler };
}

// ── Høydeprofil: skalering + baner (rent, testbart) ──────────────────────────

export interface ProfilPad { t: number; r: number; b: number; l: number }

export interface ProfilSkala {
  bredde: number;
  hoyde: number;
  pad: ProfilPad;
  minM: number;
  maxM: number;
  totalM: number;
  x(distanceM: number): number;
  y(elevM: number): number;
}

export const PROFIL_BREDDE = 320;
export const PROFIL_HOYDE = 150;
const PAD: ProfilPad = { t: 8, r: 8, b: 16, l: 36 };

/**
 * Skalering fra (avstand, høyde) til SVG-koordinater. Y-omfanget dekker ALT som
 * tegnes (terreng, krumningskorrigert terreng, siktlinje, endepunkthøyder) pluss
 * 6 % luft, slik at siktlinja aldri havner utenfor rammen når den går høyt over
 * terrenget.
 */
export function profilSkala(
  los: LosResult, bredde = PROFIL_BREDDE, hoyde = PROFIL_HOYDE, pad: ProfilPad = PAD,
): ProfilSkala {
  let min = Math.min(los.observerAmslM, los.targetAmslM);
  let max = Math.max(los.observerAmslM, los.targetAmslM);
  for (const s of los.samples) {
    if (s.terrainElevM !== null) {
      min = Math.min(min, s.terrainElevM);
      max = Math.max(max, s.terrainElevM);
    }
    if (s.effectiveElevM !== null) max = Math.max(max, s.effectiveElevM);
    min = Math.min(min, s.sightElevM);
    max = Math.max(max, s.sightElevM);
  }
  if (!(max > min)) { max = min + 10; } // flatt terreng: gi grafen en høyde
  const luft = (max - min) * 0.06;
  const minM = min - luft;
  const maxM = max + luft;
  const totalM = los.distanceM;
  const innW = bredde - pad.l - pad.r;
  const innH = hoyde - pad.t - pad.b;
  return {
    bredde, hoyde, pad, minM, maxM, totalM,
    x: (d: number) => pad.l + (totalM > 0 ? (d / totalM) * innW : 0),
    y: (e: number) => pad.t + (1 - (e - minM) / (maxM - minM)) * innH,
  };
}

export interface ProfilPunktMerke { x: number; yTerreng: number; ySikt: number }

export interface ProfilBaner {
  skala: ProfilSkala;
  /** Fylt terrengsnitt (flere delbaner ved hull i høydedata). */
  terrengFyll: string;
  /** Terrengkurven alene. */
  terrengLinje: string;
  /** Krumnings-/refraksjonskorrigert terreng — null når buen er < 1 m. */
  krumning: string | null;
  maksBulgeM: number;
  sikt: { x1: number; y1: number; x2: number; y2: number };
  hinder: ProfilPunktMerke | null;
  minKlaring: ProfilPunktMerke | null;
  hull: number;
}

/** SVG-baner for høydeprofilen. Hull i høydedata BRYTER banen (ingen gjetting). */
export function profilBaner(
  los: LosResult, bredde = PROFIL_BREDDE, hoyde = PROFIL_HOYDE, pad: ProfilPad = PAD,
): ProfilBaner {
  const sk = profilSkala(los, bredde, hoyde, pad);
  const bunn = hoyde - pad.b;
  const linjeDeler: string[] = [];
  const fyllDeler: string[] = [];
  const krumDeler: string[] = [];
  let maksBulgeM = 0;
  let hull = 0;

  let løpLinje: string[] = [];
  let løpKrum: string[] = [];
  let løpStartX = 0;
  let løpSluttX = 0;
  const lukk = () => {
    if (løpLinje.length >= 2) {
      linjeDeler.push(`M${løpLinje.join('L')}`);
      fyllDeler.push(`M${løpStartX.toFixed(1)},${bunn.toFixed(1)}L${løpLinje.join('L')}L${løpSluttX.toFixed(1)},${bunn.toFixed(1)}Z`);
      krumDeler.push(`M${løpKrum.join('L')}`);
    }
    løpLinje = []; løpKrum = [];
  };

  for (const s of los.samples) {
    if (s.terrainElevM === null || s.effectiveElevM === null) { hull++; lukk(); continue; }
    maksBulgeM = Math.max(maksBulgeM, s.bulgeM);
    const px = sk.x(s.distanceM);
    if (!løpLinje.length) løpStartX = px;
    løpSluttX = px;
    løpLinje.push(`${px.toFixed(1)},${sk.y(s.terrainElevM).toFixed(1)}`);
    løpKrum.push(`${px.toFixed(1)},${sk.y(s.effectiveElevM).toFixed(1)}`);
  }
  lukk();

  const merke = (distanceM: number): ProfilPunktMerke | null => {
    const s = los.samples.reduce<LosSample | null>((best, cur) => {
      if (cur.terrainElevM === null) return best;
      if (!best) return cur;
      return Math.abs(cur.distanceM - distanceM) < Math.abs(best.distanceM - distanceM) ? cur : best;
    }, null);
    if (!s || s.terrainElevM === null) return null;
    return { x: sk.x(s.distanceM), yTerreng: sk.y(s.terrainElevM), ySikt: sk.y(s.sightElevM) };
  };

  return {
    skala: sk,
    terrengFyll: fyllDeler.join(' '),
    terrengLinje: linjeDeler.join(' '),
    // Under ~1 m bue er de to kurvene visuelt identiske; da lyver en ekstra
    // stiplet linje om at korreksjonen betyr noe her.
    krumning: maksBulgeM >= 1 ? krumDeler.join(' ') : null,
    maksBulgeM,
    sikt: {
      x1: sk.x(0), y1: sk.y(los.observerAmslM),
      x2: sk.x(los.distanceM), y2: sk.y(los.targetAmslM),
    },
    hinder: los.obstruction ? merke(los.obstruction.distanceM) : null,
    minKlaring: Number.isNaN(los.minClearanceM) ? null : merke(los.minClearanceAtM),
    hull,
  };
}

// ── Panelet ──────────────────────────────────────────────────────────────────

export type SiktPanelStatus = 'tom' | 'venter-a' | 'venter-b' | 'laster-dem' | 'ok' | 'ingen-data' | 'feil';

export const SIKT_PANEL_TEKST: Record<SiktPanelStatus, string> = {
  tom: 'Velg observatør (A) og mål (B) i kartet.',
  'venter-a': 'Klikk i kartet: observatør (A).',
  'venter-b': 'Klikk i kartet: mål (B).',
  'laster-dem': 'Henter høydedata (Terrarium) …',
  ok: 'Regnet over ekte høydedata.',
  'ingen-data': 'Ingen høydedata for ett av punktene — kan ikke svare (ikke det samme som fri sikt).',
  feil: 'Beregningen feilet.',
};

const SRC = 'sikt';
/** Farger speilet fra theme.css (kart-paint kan ikke bruke CSS-variabler). */
const F_FRI = '#8ba888'; // --sage
const F_BLOKKERT = '#c23a3a'; // --danger
const F_UKJENT = '#8b9b8b'; // --text-secondary (ingen påstand)
const F_GULL = '#d4a03a'; // --gold

export interface SiktHooks {
  /** Delt flis-cache (gjenbrukes med dekningslaget). */
  cache?: TerrariumTileCache;
  center?: () => { lon: number; lat: number };
  alarm?: (tekst: string) => void;
}

export class SiktPanel {
  readonly el: HTMLElement;
  readonly tilstand = new SiktTilstand();
  /** Kalles når panelet åpnes/lukkes, så dock-knappen kan speile tilstanden. */
  onStateChange?: () => void;

  private open = false;

  /** Er panelet åpent? Finnes for at verktøylinja skal kunne SI det — dock-merket
   * for dette verktøyet lyste aldri, i motsetning til rutenett og trådkors. */
  get apen(): boolean { return this.open; }
  private ready = false;
  private venter = false;
  private status: SiktPanelStatus = 'tom';
  private generasjon = 0;
  private readonly cache: TerrariumTileCache;
  /** Bufret rutenett — å endre høyder skal ikke hente flisene på nytt. */
  private grid: ElevationGrid | null = null;
  private gridNøkkel = '';
  private siste: LosResult | null = null;

  private statusEl!: HTMLElement;
  private badgeEl!: HTMLElement;
  private punktEl!: HTMLElement;
  private grafEl!: HTMLElement;
  private resultEl!: HTMLElement;

  constructor(private map: MlMap, private hooks: SiktHooks = {}) {
    this.cache = hooks.cache ?? new TerrariumTileCache();
    this.el = this.build();
    if (map.loaded?.()) this.init();
    else map.on('load', () => this.init());
  }

  // ── Offentlig API ─────────────────────────────────────────────────────────

  toggle(): void {
    this.open = !this.open;
    this.el.classList.toggle('open', this.open);
    if (this.open && !this.tilstand.klar()) this.armPunkter();
    else if (!this.open) this.avbrytArming();
    this.onStateChange?.();
  }

  erApen(): boolean { return this.open; }
  statusNå(): SiktPanelStatus { return this.status; }
  sisteResultat(): LosResult | null { return this.siste; }

  /** Neste kartklikk(er) setter punktene. */
  armPunkter(): void {
    if (this.venter) return;
    armEksklusivt(this.avbrytArming); // ikke stjel klikket fra dekningslaget
    this.venter = true;
    this.setStatus(this.tilstand.neste === 'a' ? 'venter-a' : 'venter-b');
    try {
      this.map.getCanvas().style.cursor = 'crosshair';
      this.map.on('click', this.onKart);
    } catch { /* kart uten canvas (test) */ }
  }

  /** Sett et punkt direkte (kontekstmeny «Sikt herfra/hertil»). */
  settPunkt(navn: SiktPunktNavn, lon: number, lat: number): void {
    this.tilstand.sett(navn, lon, lat);
    if (!this.open) { this.open = true; this.el.classList.add('open'); this.onStateChange?.(); }
    this.oppdaterPunktTekst();
    this.tegn(null);
    if (this.tilstand.klar()) void this.beregn();
    else this.armPunkter();
  }

  /**
   * Meldes når siktelinja endrer seg (nytt punkt, nytt resultat, nullstilt), så
   * objektlista i høyrepanelet kan tegnes på nytt. Uten den ville linja ligget i
   * kartet uten å finnes i lista — nettopp «maling uten identitet» (rapportens
   * §6.5).
   */
  onEndring?: () => void;

  nullstill(): void {
    this.generasjon++;
    this.tilstand.nullstill();
    this.grid = null; this.gridNøkkel = ''; this.siste = null;
    this.oppdaterPunktTekst();
    this.tegn(null);
    this.grafEl.replaceChildren();
    this.resultEl.replaceChildren();
    this.setStatus('tom');
  }

  /** Hent høydedata (om nødvendig) og regn siktlinja. */
  async beregn(): Promise<void> {
    const { a, b } = this.tilstand;
    if (!a || !b) { this.setStatus('tom'); return; }
    const gen = ++this.generasjon;
    const dist = avstandM(a, b);
    const celleM = celleForAvstand(dist);
    const nøkkel = `${nf(a.lat, 5)},${nf(a.lon, 5)}|${nf(b.lat, 5)},${nf(b.lon, 5)}|${celleM}`;

    try {
      if (this.gridNøkkel !== nøkkel || !this.grid) {
        this.setStatus('laster-dem');
        const margen = Math.max(500, celleM * 4);
        const hd = await hentHoydedataForBoks(boksRundtPunkter([a, b], margen), celleM, { cache: this.cache });
        if (gen !== this.generasjon) return;
        this.grid = hd.grid;
        this.gridNøkkel = nøkkel;
      }
      // Ett profilpunkt per halve DEM-celle, men aldri under 64 eller over 512.
      const samples = Math.min(512, Math.max(64, Math.round((dist / celleM) * 2)));
      const los = lineOfSightDetailed(this.grid, a, b, {
        observerHeightM: this.tilstand.observatorHoydeM,
        targetHeightM: this.tilstand.malHoydeM,
        requiredClearanceM: this.tilstand.marginM,
        samples,
      });
      if (gen !== this.generasjon) return;
      if (!los) {
        this.siste = null;
        this.grafEl.replaceChildren();
        this.resultEl.replaceChildren();
        this.tegn(null);
        this.setStatus('ingen-data');
        return;
      }
      this.siste = los;
      const sam = siktSammendrag(los, this.tilstand.marginM);
      this.visSammendrag(sam);
      this.tegnGraf(los, sam.status);
      this.tegn(los);
      this.setStatus('ok');
    } catch (e) {
      if (gen !== this.generasjon) return;
      this.setStatus('feil');
      this.leggAdvarsel(e instanceof Error ? e.message : String(e));
    }
  }

  // ── Kartlag ───────────────────────────────────────────────────────────────

  private init(): void {
    if (this.ready) return;
    try {
      if (!this.map.getSource(SRC)) {
        this.map.addSource(SRC, { type: 'geojson', data: featureCollection([]) as never });
        this.map.addLayer({
          id: 'sikt-linje', type: 'line', source: SRC,
          filter: ['==', ['geometry-type'], 'LineString'],
          paint: {
            'line-width': 3,
            // 'ukjent' må ALDRI se ut som fri sikt — nøytral grå til vi har et svar.
            'line-color': ['match', ['get', 'del'], 'blokkert', F_BLOKKERT, 'fri', F_FRI, F_UKJENT],
          },
        });
        this.map.addLayer({
          id: 'sikt-punkt', type: 'circle', source: SRC,
          filter: ['==', ['get', 'rolle'], 'punkt'],
          paint: {
            'circle-radius': 5, 'circle-color': F_GULL,
            'circle-stroke-color': '#0a0c0a', 'circle-stroke-width': 2,
          },
        });
        // Hindringspunktet skal ikke være til å ta feil av.
        this.map.addLayer({
          id: 'sikt-hinder', type: 'circle', source: SRC,
          filter: ['==', ['get', 'rolle'], 'hinder'],
          paint: {
            'circle-radius': 8, 'circle-color': 'rgba(0,0,0,0)',
            'circle-stroke-color': F_BLOKKERT, 'circle-stroke-width': 3,
          },
        });
        this.map.addLayer({
          id: 'sikt-merke', type: 'symbol', source: SRC,
          filter: ['has', 'merke'],
          layout: {
            'text-field': ['get', 'merke'], 'text-font': ['Open Sans Regular'],
            'text-size': 11, 'text-offset': [0, -1.4], 'text-anchor': 'bottom',
            'text-allow-overlap': true,
          },
          paint: { 'text-color': '#e8ede8', 'text-halo-color': '#0a0c0a', 'text-halo-width': 1.4 },
        });
      }
      this.ready = true;
    } catch { /* kart uten lag-API (test) */ }
  }

  /**
   * Tegn linja delt i FRI del (A → hindring) og BLOKKERT del (hindring → B), pluss
   * endepunkt og hindringsmarkør. Delingen er hele poenget: operatøren ser HVOR
   * sikten tar slutt, ikke bare at den gjør det.
   */
  private tegn(los: LosResult | null): void {
    this.init();
    const { a, b } = this.tilstand;
    const feats: GeoFeature[] = [];
    if (a) feats.push(punktFeature(a, 'A'));
    if (b) feats.push(punktFeature(b, 'B'));
    if (a && b) {
      const hinder = los?.obstruction ?? null;
      if (hinder) {
        feats.push(
          linje([[a.lon, a.lat], [hinder.lon, hinder.lat]], 'fri'),
          linje([[hinder.lon, hinder.lat], [b.lon, b.lat]], 'blokkert'),
          {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [hinder.lon, hinder.lat] },
            properties: { rolle: 'hinder', merke: `Hinder ${fmtHoyde(hinder.terrainElevM)}` },
          },
        );
      } else {
        feats.push(linje([[a.lon, a.lat], [b.lon, b.lat]], los ? 'fri' : 'ukjent'));
      }
    }
    try {
      (this.map.getSource(SRC) as GeoJSONSource | undefined)
        ?.setData(featureCollection(feats) as never);
    } catch { /* ingen kilde (test) */ }
  }

  private onKart = (e: MapMouseEvent) => {
    if (!this.venter) return;
    const satt = this.tilstand.settPunkt(e.lngLat.lng, e.lngLat.lat);
    this.oppdaterPunktTekst();
    if (satt === 'a') {
      this.grafEl.replaceChildren();
      this.resultEl.replaceChildren();
      this.siste = null;
      this.tegn(null);
      this.setStatus('venter-b');
      return;
    }
    this.avbrytArming();
    this.tegn(null);
    void this.beregn();
  };

  /**
   * MÅ være en arrow-property, ikke en metode: referansen sendes til
   * `armEksklusivt()` og kalles av arbiteren som en bar funksjon. Som vanlig
   * metode ville `this` vært undefined (ES-moduler er strict) → TypeError, og
   * klikk-håndtereren ville aldri blitt fjernet, så siktelinja fortsatte å
   * stjele kartklikk fra dekningslaget. Samme form som i dekning.ts.
   */
  private avbrytArming = (): void => {
    frigiArming(this.avbrytArming); // gi fra seg arbiter-plassen, ellers henger den
    if (!this.venter) return;
    this.venter = false;
    try {
      this.map.getCanvas().style.cursor = '';
      this.map.off('click', this.onKart);
    } catch { /* ignorer */ }
  };

  // ── DOM ───────────────────────────────────────────────────────────────────

  private build(): HTMLElement {
    const p = div('report-composer sikt-panel');
    const head = div('report-head');
    head.append(span('report-title', 'Siktelinje — OP mot mål'));
    const x = document.createElement('button');
    x.className = 'report-x'; x.type = 'button'; x.title = 'Lukk';
    x.textContent = '×'; x.onclick = () => this.toggle();
    head.append(x);
    p.append(head);

    this.badgeEl = div('sikt-badge');
    p.append(this.badgeEl);

    this.punktEl = div('sikt-punkter');
    p.append(this.punktEl);

    const knapper = div('sikt-knapper');
    knapper.append(
      knapp('Velg punkter i kartet', 'Klikk observatør (A), så mål (B)', () => this.armPunkter()),
      knapp('Bytt A ↔ B', 'Snu spørsmålet: ser målet observatøren?', () => {
        this.tilstand.bytt();
        this.oppdaterPunktTekst();
        if (this.tilstand.klar()) void this.beregn();
      }),
      knapp('Nullstill', 'Fjern linja fra kartet', () => this.nullstill()),
    );
    p.append(knapper);

    const t = this.tilstand;
    const form = div('report-form');
    form.append(
      this.tallFelt('Observatør over bakken (m)', () => t.observatorHoydeM,
        (v) => { t.observatorHoydeM = v; }, 0, 300, 0.1, OBSERVATOR_FORVALG),
      this.tallFelt('Mål over bakken (m)', () => t.malHoydeM,
        (v) => { t.malHoydeM = v; }, 0, 5000, 0.1, MAL_FORVALG),
      this.tallFelt('Margin for skog/bebyggelse (m)', () => t.marginM,
        (v) => { t.marginM = v; }, 0, 100, 1),
    );
    p.append(form);

    const beregn = document.createElement('button');
    beregn.type = 'button'; beregn.className = 'report-submit';
    beregn.textContent = 'Beregn på nytt';
    beregn.onclick = () => void this.beregn();
    p.append(beregn);

    this.statusEl = div('sikt-status');
    p.append(this.statusEl);

    this.grafEl = div('sikt-graf');
    p.append(this.grafEl);
    p.append(this.grafForklaring());

    this.resultEl = div('sikt-result');
    p.append(this.resultEl);

    const note = div('sikt-note');
    note.textContent = DEM_KILDE_NOTE;
    p.append(note);

    this.oppdaterPunktTekst();
    this.setStatus('tom');
    return p;
  }

  private grafForklaring(): HTMLElement {
    const l = div('sikt-legend');
    for (const [cls, tekst] of [
      ['sikt-lg-terreng', 'Terreng (kote)'],
      ['sikt-lg-krum', 'Terreng + jordkrumning/refraksjon (k = 4/3)'],
      ['sikt-lg-sikt', 'Siktlinje A→B'],
    ] as [string, string][]) {
      const rad = div('sikt-legend-rad');
      rad.append(div(`sikt-sw ${cls}`), span('sikt-legend-t', tekst));
      l.append(rad);
    }
    return l;
  }

  private tallFelt(
    etikett: string, les: () => number, skriv: (v: number) => void,
    min: number, max: number, steg: number, forvalg?: [string, number][],
  ): HTMLElement {
    const f = div('report-field');
    f.append(span('report-field-label', etikett));
    const rad = div('sikt-felt-rad');
    const inp = document.createElement('input');
    inp.type = 'number'; inp.className = 'report-input sikt-num';
    inp.min = String(min); inp.max = String(max); inp.step = String(steg);
    inp.value = String(les());
    const bruk = (v: number) => {
      skriv(Math.min(max, Math.max(min, v)));
      if (this.tilstand.klar()) void this.beregn();
    };
    inp.onchange = () => { const v = Number(inp.value); if (Number.isFinite(v)) bruk(v); };
    rad.append(inp);
    if (forvalg) {
      const sel = document.createElement('select');
      sel.className = 'report-input sikt-forvalg';
      sel.title = 'Forhåndsvalg';
      const tom = document.createElement('option');
      tom.value = ''; tom.textContent = 'Forvalg …';
      sel.append(tom);
      for (const [navn, verdi] of forvalg) {
        const o = document.createElement('option');
        o.value = String(verdi); o.textContent = navn;
        sel.append(o);
      }
      sel.onchange = () => {
        if (sel.value === '') return;
        const v = Number(sel.value);
        inp.value = String(v);
        sel.value = '';
        bruk(v);
      };
      rad.append(sel);
    }
    f.append(rad);
    return f;
  }

  private setStatus(s: SiktPanelStatus): void {
    this.status = s;
    this.statusEl.textContent = SIKT_PANEL_TEKST[s];
    this.statusEl.className = `sikt-status sikt-${s}`;
    if (s !== 'ok') {
      this.badgeEl.className = 'sikt-badge';
      this.badgeEl.textContent = s === 'ingen-data' ? SIKT_STATUS_TEKST.ukjent : '';
    }
    if (s === 'ingen-data' || s === 'feil') this.hooks.alarm?.(SIKT_PANEL_TEKST[s]);
    // ÉN melding om at siktelinja er endret. Statusen skifter ved hvert punkt,
    // hver beregning og hver nullstilling — altså nøyaktig når objektlista må
    // tegnes på nytt. Å melde fra flere steder ville gitt to eiere av samme
    // hendelse, som er selve feilklassen denne bølgen rydder opp i.
    this.onEndring?.();
  }

  private oppdaterPunktTekst(): void {
    this.punktEl.replaceChildren();
    for (const navn of ['a', 'b'] as SiktPunktNavn[]) {
      const p = this.tilstand[navn];
      const rad = div('sikt-punkt-rad' + (this.tilstand.neste === navn ? ' neste' : ''));
      rad.append(span('sikt-punkt-nav', navn === 'a' ? 'A · observatør' : 'B · mål'));
      rad.append(span('sikt-punkt-pos', p ? mgrsEllerGrad(p) : 'ikke satt'));
      this.punktEl.append(rad);
    }
    if (this.tilstand.klar()) {
      const d = div('sikt-punkt-avstand');
      d.textContent = `Avstand ${fmtAvstand(this.tilstand.avstandM())}`;
      this.punktEl.append(d);
    }
  }

  private visSammendrag(sam: SiktSammendrag): void {
    this.badgeEl.className = `sikt-badge vis ${sam.status}`;
    this.badgeEl.textContent = sam.tittel;
    this.resultEl.replaceChildren();
    for (const [k, v] of sam.rader) {
      const r = div('report-kv');
      r.append(span('report-k', k), span('report-v', v));
      this.resultEl.append(r);
    }
    for (const a of sam.advarsler) this.leggAdvarsel(a);
  }

  private leggAdvarsel(tekst: string): void {
    const a = div('sikt-advarsel');
    a.textContent = tekst;
    this.resultEl.append(a);
  }

  /** Tegn høydeprofilen som SVG. Ren DOM — ingen chart-avhengighet. */
  private tegnGraf(los: LosResult, status: SiktStatus): void {
    const b = profilBaner(los);
    const sk = b.skala;
    const svg = svgEl('svg', {
      class: `sikt-svg ${status}`,
      viewBox: `0 0 ${sk.bredde} ${sk.hoyde}`,
      role: 'img',
      'aria-label': `Høydeprofil ${fmtAvstand(los.distanceM)}, ${SIKT_STATUS_TEKST[status]}`,
    });

    // Ramme + y-akse-etiketter (kote øverst/nederst).
    svg.append(svgEl('line', {
      class: 'sikt-akse',
      x1: sk.pad.l, y1: sk.hoyde - sk.pad.b, x2: sk.bredde - sk.pad.r, y2: sk.hoyde - sk.pad.b,
    }));
    svg.append(svgEl('line', {
      class: 'sikt-akse', x1: sk.pad.l, y1: sk.pad.t, x2: sk.pad.l, y2: sk.hoyde - sk.pad.b,
    }));
    svg.append(tekst(sk.pad.l - 4, sk.pad.t + 4, fmtHoyde(sk.maxM), 'sikt-tick end'));
    svg.append(tekst(sk.pad.l - 4, sk.hoyde - sk.pad.b, fmtHoyde(sk.minM), 'sikt-tick end'));
    svg.append(tekst(sk.pad.l, sk.hoyde - 3, '0', 'sikt-tick'));
    svg.append(tekst(sk.bredde - sk.pad.r, sk.hoyde - 3, fmtAvstand(sk.totalM), 'sikt-tick end'));

    if (b.terrengFyll) svg.append(svgEl('path', { class: 'sikt-terreng-fyll', d: b.terrengFyll }));
    if (b.terrengLinje) svg.append(svgEl('path', { class: 'sikt-terreng-linje', d: b.terrengLinje }));
    if (b.krumning) svg.append(svgEl('path', { class: 'sikt-krum-linje', d: b.krumning }));

    if (b.minKlaring && !b.hinder) {
      svg.append(svgEl('line', {
        class: 'sikt-klaring',
        x1: b.minKlaring.x, y1: b.minKlaring.yTerreng, x2: b.minKlaring.x, y2: b.minKlaring.ySikt,
      }));
    }
    svg.append(svgEl('line', {
      class: 'sikt-siktlinje', x1: b.sikt.x1, y1: b.sikt.y1, x2: b.sikt.x2, y2: b.sikt.y2,
    }));
    if (b.hinder) {
      svg.append(svgEl('line', {
        class: 'sikt-hinder-stolpe',
        x1: b.hinder.x, y1: b.hinder.yTerreng, x2: b.hinder.x, y2: b.hinder.ySikt,
      }));
      svg.append(svgEl('circle', { class: 'sikt-hinder-punkt', cx: b.hinder.x, cy: b.hinder.yTerreng, r: 3.5 }));
    }
    svg.append(svgEl('circle', { class: 'sikt-ende', cx: b.sikt.x1, cy: b.sikt.y1, r: 3 }));
    svg.append(svgEl('circle', { class: 'sikt-ende', cx: b.sikt.x2, cy: b.sikt.y2, r: 3 }));
    svg.append(tekst(b.sikt.x1 + 4, b.sikt.y1 - 5, 'A', 'sikt-ende-lbl'));
    svg.append(tekst(b.sikt.x2 - 4, b.sikt.y2 - 5, 'B', 'sikt-ende-lbl end'));

    this.grafEl.replaceChildren(svg);
    if (b.krumning === null) {
      const n = div('sikt-graf-note');
      n.textContent = `Jordkrumningen er ${nf(b.maksBulgeM, 2)} m på denne avstanden — kurvene sammenfaller.`;
      this.grafEl.append(n);
    }
  }
}

// ── Små hjelpere ─────────────────────────────────────────────────────────────

function punktFeature(p: LatLon, merke: string): GeoFeature {
  return {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
    properties: { rolle: 'punkt', merke },
  };
}

function linje(coords: [number, number][], del: 'fri' | 'blokkert' | 'ukjent'): GeoFeature {
  return { type: 'Feature', geometry: { type: 'LineString', coordinates: coords }, properties: { del } };
}

/** MGRS når punktet er i UTM-sone, ellers desimalgrader. */
export function mgrsEllerGrad(p: LatLon): string {
  try { return mgrsForward([p.lon, p.lat], 5); } catch { return `${nf(p.lat, 5)}, ${nf(p.lon, 5)}`; }
}

function svgEl(tag: string, attrs: Record<string, string | number>): SVGElement {
  const e = document.createElementNS('http://www.w3.org/2000/svg', tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  return e;
}
function tekst(x: number, y: number, innhold: string, cls: string): SVGElement {
  const e = svgEl('text', { x, y, class: cls });
  e.textContent = innhold;
  return e;
}
function div(cls: string): HTMLElement {
  const e = document.createElement('div');
  e.className = cls;
  return e;
}
function span(cls: string, t: string): HTMLElement {
  const e = document.createElement('span');
  e.className = cls;
  e.textContent = t;
  return e;
}
function knapp(etikett: string, tittel: string, run: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button'; b.className = 'sikt-btn'; b.title = tittel;
  b.textContent = etikett; b.onclick = run;
  return b;
}
